import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PlusCircle, Edit, Trash2, Save, X, Coins, DollarSign, Tag, Star, ArrowUpDown } from 'lucide-react';
import { motion } from 'framer-motion';

import adminCreditPackageService, { CreditPackage, CreditPackageCreate, CreditPackageUpdate } from '../../services/adminCreditPackageService';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';

export const CreditPackageManager: React.FC = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<CreditPackage | null>(null);
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<CreditPackageCreate>();

  // Fetch credit packages
  const { data: packages, isLoading } = useQuery({
    queryKey: ['admin-credit-packages'],
    queryFn: () => adminCreditPackageService.getCreditPackages(),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreditPackageCreate) => adminCreditPackageService.createCreditPackage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-credit-packages'] });
      toast.success('Credit package created successfully');
      setIsCreateDialogOpen(false);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create credit package');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreditPackageUpdate }) =>
      adminCreditPackageService.updateCreditPackage(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-credit-packages'] });
      toast.success('Credit package updated successfully');
      setEditingPackage(null);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update credit package');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminCreditPackageService.deleteCreditPackage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-credit-packages'] });
      toast.success('Credit package deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete credit package');
    },
  });

  const onSubmit = (data: CreditPackageCreate) => {
    if (editingPackage) {
      updateMutation.mutate({ id: editingPackage.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (pkg: CreditPackage) => {
    setEditingPackage(pkg);
    setValue('name', pkg.name);
    setValue('description', pkg.description || '');
    setValue('credits', pkg.credits);
    setValue('bonus_credits', pkg.bonus_credits);
    setValue('price_cents', pkg.price_cents);
    setValue('currency', pkg.currency);
    setValue('is_active', pkg.is_active);
    setValue('is_popular', pkg.is_popular);
    setValue('sort_order', pkg.sort_order);
    setIsCreateDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this credit package?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setEditingPackage(null);
    reset();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <Coins className="w-5 h-5" />
              <span>Credit Packages</span>
            </CardTitle>
            <CardDescription>
              Manage credit packages available for purchase
            </CardDescription>
          </div>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <PlusCircle className="w-4 h-4 mr-2" />
            Add Package
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8">Loading...</div>
        ) : packages && packages.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Credits</TableHead>
                <TableHead>Bonus</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Popular</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packages
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((pkg) => (
                  <TableRow key={pkg.id}>
                    <TableCell className="font-medium">{pkg.name}</TableCell>
                    <TableCell>{pkg.credits}</TableCell>
                    <TableCell>{pkg.bonus_credits > 0 ? `+${pkg.bonus_credits}` : '-'}</TableCell>
                    <TableCell>€{pkg.price_dollars.toFixed(2)}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs ${pkg.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {pkg.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {pkg.is_popular && <Star className="w-4 h-4 text-yellow-500" />}
                    </TableCell>
                    <TableCell>{pkg.sort_order}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(pkg)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(pkg.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No credit packages found. Create one to get started.
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={handleCloseDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingPackage ? 'Edit Credit Package' : 'Create Credit Package'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Package Name *</Label>
                  <Input
                    id="name"
                    {...register('name', { required: 'Name is required' })}
                    placeholder="e.g., Starter Pack"
                  />
                  {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
                </div>

                <div>
                  <Label htmlFor="credits">Credits *</Label>
                  <Input
                    id="credits"
                    type="number"
                    step="0.01"
                    {...register('credits', { required: 'Credits is required', valueAsNumber: true })}
                    placeholder="100"
                  />
                  {errors.credits && <p className="text-sm text-red-500">{errors.credits.message}</p>}
                </div>

                <div>
                  <Label htmlFor="bonus_credits">Bonus Credits</Label>
                  <Input
                    id="bonus_credits"
                    type="number"
                    step="0.01"
                    {...register('bonus_credits', { valueAsNumber: true })}
                    placeholder="0"
                    defaultValue={0}
                  />
                </div>

                <div>
                  <Label htmlFor="price_cents">Price (cents) *</Label>
                  <Input
                    id="price_cents"
                    type="number"
                    {...register('price_cents', { required: 'Price is required', valueAsNumber: true })}
                    placeholder="999 (for €9.99)"
                  />
                  {errors.price_cents && <p className="text-sm text-red-500">{errors.price_cents.message}</p>}
                </div>

                <div>
                  <Label htmlFor="currency">Currency</Label>
                  <Input
                    id="currency"
                    {...register('currency')}
                    placeholder="EUR"
                    defaultValue="EUR"
                    maxLength={3}
                  />
                </div>

                <div>
                  <Label htmlFor="sort_order">Sort Order</Label>
                  <Input
                    id="sort_order"
                    type="number"
                    {...register('sort_order', { valueAsNumber: true })}
                    placeholder="0"
                    defaultValue={0}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  placeholder="Brief description of the package"
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    {...register('is_active')}
                    defaultChecked={true}
                    className="rounded"
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">Active</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_popular"
                    {...register('is_popular')}
                    className="rounded"
                  />
                  <Label htmlFor="is_popular" className="cursor-pointer">Mark as Popular</Label>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  <Save className="w-4 h-4 mr-2" />
                  {editingPackage ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
